import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input } from '../components/common/Input';
import { Badge } from '../components/common/Badge';
import { Spinner } from '../components/common/Spinner';
import { Modal } from '../components/common/Modal';
import { CheckCircle, AlertCircle } from 'lucide-react';

export const ShowcasePage = () => {
  const [showModal, setShowModal] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const testingState = useSelector(state => state.testing);

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-6xl mx-auto mb-8">
        <h1 className="text-4xl font-bold text-slate-800 mb-2">
          Looey Web App - Phase 4 🚀
        </h1>
        <p className="text-slate-600">
          Foundation Complete - Component Showcase
        </p>
        <div className="mt-4 flex gap-2">
          <Badge variant="success">✅ Redux Connected</Badge>
          <Badge variant="primary">✅ Tailwind Working</Badge>
          <Badge variant="info">✅ 7 Components Built</Badge>
        </div>
      </div>

      <div className="max-w-6xl mx-auto space-y-6">
        {/* Status Card */}
        <Card className="p-6">
          <h2 className="text-2xl font-semibold text-slate-800 mb-4">
            🎯 What's Ready
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="text-mint-500 mt-1" size={20} />
              <div>
                <h3 className="font-medium text-slate-800">Redux Store</h3>
                <p className="text-sm text-slate-600">5 slices: auth, testing, agent, todos, credentials</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="text-mint-500 mt-1" size={20} />
              <div>
                <h3 className="font-medium text-slate-800">API Services</h3>
                <p className="text-sm text-slate-600">6 services with auth interceptors</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="text-mint-500 mt-1" size={20} />
              <div>
                <h3 className="font-medium text-slate-800">UI Components</h3>
                <p className="text-sm text-slate-600">Button, Card, Input, Badge, Spinner, Modal, EmptyState</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="text-mint-500 mt-1" size={20} />
              <div>
                <h3 className="font-medium text-slate-800">Routing & Layout</h3>
                <p className="text-sm text-slate-600">React Router with Header navigation</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Component Demo */}
        <Card className="p-6">
          <h2 className="text-2xl font-semibold text-slate-800 mb-4">
            🎨 Component Showcase
          </h2>

          {/* Buttons */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-slate-700 mb-3">Buttons</h3>
            <div className="flex flex-wrap gap-3">
              <Button variant="primary">Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="danger">Danger</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button disabled>Disabled</Button>
            </div>
          </div>

          {/* Badges */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-slate-700 mb-3">Badges</h3>
            <div className="flex flex-wrap gap-2">
              <Badge variant="default">Default</Badge>
              <Badge variant="primary">Primary</Badge>
              <Badge variant="success">Success</Badge>
              <Badge variant="warning">Warning</Badge>
              <Badge variant="danger">Danger</Badge>
              <Badge variant="info">Info</Badge>
            </div>
          </div>

          {/* Input */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-slate-700 mb-3">Input</h3>
            <div className="max-w-md">
              <Input
                label="Email Address"
                type="email"
                placeholder="you@example.com"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
            </div>
          </div>

          {/* Spinner */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-slate-700 mb-3">Spinner</h3>
            <div className="flex gap-4 items-center">
              <Spinner size="sm" />
              <Spinner size="md" />
              <Spinner size="lg" />
            </div>
          </div>

          {/* Modal Demo */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-slate-700 mb-3">Modal</h3>
            <Button onClick={() => setShowModal(true)}>
              Open Modal
            </Button>
          </div>
        </Card>

        {/* Next Steps */}
        <Card className="p-6">
          <h2 className="text-2xl font-semibold text-slate-800 mb-4">
            ✅ Completed & 📋 Next Steps
          </h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="text-mint-500 mt-1" size={20} />
              <div>
                <h3 className="font-medium text-slate-800">✅ Todo List Interface</h3>
                <p className="text-sm text-slate-600">
                  Full CRUD with filters, search, and detail view
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <AlertCircle className="text-periwinkle-500 mt-1" size={20} />
              <div>
                <h3 className="font-medium text-slate-800">Next: Testing Page (PRIMARY FEATURE)</h3>
                <p className="text-sm text-slate-600">
                  Create EmailTester component to paste and analyze emails
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <AlertCircle className="text-periwinkle-500 mt-1" size={20} />
              <div>
                <h3 className="font-medium text-slate-800">Then: AgentMonitor</h3>
                <p className="text-sm text-slate-600">
                  Real-time execution tracking with polling
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Redux State Demo */}
        <Card className="p-6">
          <h2 className="text-2xl font-semibold text-slate-800 mb-4">
            🔄 Redux State (Testing Slice)
          </h2>
          <pre className="bg-slate-100 p-4 rounded-lg overflow-x-auto text-xs">
            {JSON.stringify(testingState, null, 2)}
          </pre>
        </Card>
      </div>

      {/* Modal Example */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Example Modal"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-slate-600">
            This is an example modal dialog. It supports:
          </p>
          <ul className="list-disc list-inside text-slate-600 space-y-2">
            <li>Click outside to close</li>
            <li>Multiple sizes (sm, md, lg, xl)</li>
            <li>Scrollable content area</li>
            <li>Body scroll lock when open</li>
          </ul>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => setShowModal(false)}>
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
